import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgSmartAccount } from '~/types'

export const useArchiveSmartAccount = (smartAccount: OrgSmartAccount) => {
  const { serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const archiveSmartAccount = useCallback(
    async ({ onSuccess }: { onSuccess?: () => void } = {}) => {
      await serverPutRequest({
        path: '/api/smart_accounts/edit/archive',
        params: { smartAccountId: smartAccount.id },
        successMessage: 'Account successfully archived',
        errorMessage: 'Failed to archive account',
      })

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey[1]?.orgId === activeOrgId
          )
        },
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
      })

      onSuccess?.()
    },
    [
      serverPutRequest,
      smartAccount.id,
      smartAccount.address,
      queryClient,
      activeOrgId,
    ],
  )

  const unarchiveSmartAccount = useCallback(
    async ({ onSuccess }: { onSuccess?: () => void } = {}) => {
      await serverPutRequest({
        path: '/api/smart_accounts/edit/unarchive',
        params: { smartAccountId: smartAccount.id },
        successMessage: 'Account successfully restored',
        errorMessage: 'Failed to restore account',
      })

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
      })

      onSuccess?.()
    },
    [
      serverPutRequest,
      smartAccount.id,
      smartAccount.address,
      queryClient,
      activeOrgId,
    ],
  )

  return { archiveSmartAccount, unarchiveSmartAccount }
}
